// ============================================================================
// Agent Chat APIs
//
// GraphQL calls for the AutoMate agent:
//   - `sendAgentMessage`  — mutation that dispatches a user question and
//                            returns a conversationId + sessionId
//   - `searchAgent`       — mutation that dispatches a one-shot search query
//                            and returns a conversationId + sessionId
//   - `autocompleteAgent` — synchronous mutation that returns a list of
//                            completion suggestions for a typed prefix
//   - `quickSearch`       — synchronous mutation that returns structured
//                            vector-similarity search results
//   - `submitFeedback`    — synchronous mutation that submits per-turn
//                            thumbs-up / thumbs-down feedback
//   - `onAgentResponse`   — subscription that streams response chunks back
//
// All calls go through the direct AppSync client in `./AISearchAppSync`,
// which talks to the dedicated agent AppSync API without using Amplify's
// global config. See that file's header for the rationale.
//
// The subscription payload is re-shaped to match Amplify's traditional
// subscription envelope (`{ value: { data: ... } }`) so React callers keep
// a single familiar response shape.
// ============================================================================

import { aiGraphqlMutation, aiGraphqlSubscribe } from 'src/utilities/AISearchAppSync';
import { createDebugLog } from 'src/Ateam-BI/AutoMate/utils/aiDebugLog';

const debugLog = createDebugLog('[autocompleteAgent]');

/**
 * Send a user message to the agent.
 *
 * @param {string} message    – user's question
 * @param {string|null} sessionId – optional conversation session to continue.
 *                                  Pass null to start a fresh conversation.
 * @param {(result: any) => void} onSuccess – called with the AppSync response
 *      `{ data: { sendAgentMessage: { conversationId, sessionId } } }`.
 * @param {(err: any) => void}    onFailure – called if the mutation errors
 *      out at transport level or returns GraphQL errors.
 * @param {{ memoryId?: string, persona?: object }} [options] – optional options bag.
 *      `memoryId` is passed to the backend so Bedrock can associate long-term
 *      memory with this user (if memory is enabled on the agent).
 *      `persona` is the PersonaInput payload containing personaClassification,
 *      alias, jobFamilyName, jobProfileName, and jobLevel.
 */
export const sendAgentMessage = async (message, sessionId, onSuccess, onFailure, { memoryId, persona } = {}) => {
  const query = `mutation MyMutation($input: SendAgentMessageInput!${persona ? ', $persona: PersonaInput' : ''}) {
    sendAgentMessage(input: $input${persona ? ', persona: $persona' : ''}) {
      conversationId
      sessionId
    }
  }`;
  const variables = {
    input: {
      message,
      ...(sessionId && { sessionId }),
      ...(memoryId && { memoryId }),
    },
    ...(persona && { persona }),
  };
  try {
    const result = await aiGraphqlMutation(query, variables);
    if (result?.errors) {
      if (onFailure) onFailure(result);
      return;
    }
    if (onSuccess) onSuccess(result);
  } catch (error) {
    if (onFailure) onFailure(error);
  }
};

/**
 * Fire a one-shot search query at the agent.
 *
 * Paired with `onAgentResponse` filtered by the returned `conversationId`.
 * The backend publishes exactly two chunks for a successful search: one
 * `STREAMING` chunk carrying the full answer, then one `COMPLETE` chunk
 * carrying the sources list (see `SearchPopup` for the matching UI).
 *
 * @param {string} query – user's search query
 * @param {number|null|undefined} maxResults – optional cap on the number of
 *      sources returned with the terminal chunk. Omit / pass null to let the
 *      backend choose its default.
 * @param {string|null|undefined} searchType – optional KB retrieval mode
 *      (e.g. `HYBRID`, `SEMANTIC`). Omit / pass null to use the backend default.
 * @param {(result: any) => void} onSuccess – called with the AppSync response
 *      `{ data: { searchAgent: { conversationId, sessionId } } }`.
 * @param {(err: any) => void}    onFailure – called if the mutation errors
 *      out at transport level or returns GraphQL errors.
 * @param {{ persona?: object }} [options] – optional options bag.
 *      `persona` is the PersonaInput payload containing personaClassification,
 *      alias, jobFamilyName, jobProfileName, and jobLevel.
 */
export const searchAgent = async (query, maxResults, searchType, onSuccess, onFailure, { persona } = {}) => {
  const mutation = `mutation MyMutation($input: SearchAgentInput!${persona ? ', $persona: PersonaInput' : ''}) {
    searchAgent(input: $input${persona ? ', persona: $persona' : ''}) {
      conversationId
      sessionId
    }
  }`;
  const variables = {
    input: {
      query,
      ...(maxResults != null && { maxResults }),
      ...(searchType && { searchType }),
    },
    ...(persona && { persona }),
  };
  try {
    const result = await aiGraphqlMutation(mutation, variables);
    if (result?.errors) {
      if (onFailure) onFailure(result);
      return;
    }
    if (onSuccess) onSuccess(result);
  } catch (error) {
    if (onFailure) onFailure(error);
  }
};

/**
 * Fire a synchronous autocomplete request against the agent.
 *
 * Unlike `searchAgent` / `sendAgentMessage` this path is fully synchronous:
 * the `autocompleteAgent` mutation is resolved by a dedicated Lambda data
 * source that calls Bedrock in non-streaming mode and returns the full
 * suggestions list in a single GraphQL response. No subscription, no chunks.
 * Async/await fits the shape naturally, so we skip the callback pattern used
 * by `searchAgent`.
 *
 * Cancellation: pass an `AbortSignal` on the options bag to cancel the
 * in-flight fetch when a new keystroke supersedes this request. The signal
 * is forwarded to `aiGraphqlMutation`, which forwards it to `fetch()`, so
 * aborting actually cuts the network round-trip — not just the callback.
 * An aborted request rethrows the underlying `AbortError` so the caller can
 * distinguish cancellation from a real error.
 *
 * Error handling: on transport failure or a GraphQL `errors` payload, this
 * throws rather than returning a sentinel. `useAgentSuggestions` catches and
 * clears suggestions silently (Requirement 10.6 / 11.10).
 *
 * @param {string} prefix – user's typed prefix (trimmed + validated by the
 *      backend; this wrapper passes it through verbatim)
 * @param {number|null|undefined} maxResults – optional cap on suggestions.
 *      Omit / pass null to let the backend use its default (10, hard cap 25).
 * @param {{ signal?: AbortSignal }} [options] – optional options bag. When
 *      `signal` is provided, it's forwarded to the underlying `fetch()` so
 *      the network request can be cancelled (Requirement 11.14).
 * @returns {Promise<{ data: { autocompleteAgent: { suggestions: string[], correlationId: string | null } } }>}
 *      The AppSync response envelope.
 * @throws the underlying `AbortError` when cancelled, or an `Error` carrying
 *      the GraphQL `errors` payload on a non-transport failure.
 */
export const autocompleteAgent = async (prefix, maxResults, { signal } = {}) => {
  const query = `mutation MyMutation($input: AutocompleteAgentInput!) {
    autocompleteAgent(input: $input) {
      suggestions
      correlationId
    }
  }`;
  const variables = {
    input: {
      prefix,
      ...(maxResults != null && { maxResults }),
    },
  };
  debugLog('dispatch', { prefixLength: (prefix || '').length, maxResults });
  try {
    const result = await aiGraphqlMutation(query, variables, { signal });
    if (result?.errors) {
      debugLog('graphql errors', result.errors);
      const err = new Error('autocompleteAgent returned GraphQL errors');
      err.graphqlErrors = result.errors;
      throw err;
    }
    debugLog('resolved', {
      resultCount: result?.data?.autocompleteAgent?.suggestions?.length ?? 0,
      correlationId: result?.data?.autocompleteAgent?.correlationId ?? null,
    });
    return result;
  } catch (error) {
    // Preserve AbortError so the caller can distinguish cancellation from a
    // real transport error. Fetch rejects with a DOMException named
    // 'AbortError' when the signal fires mid-flight.
    if (error && error.name === 'AbortError') {
      debugLog('aborted');
      throw error;
    }
    debugLog('transport error', error);
    throw error;
  }
};

/**
 * Fire a synchronous quick-search request against the agent.
 *
 * Like `autocompleteAgent`, this path is fully synchronous: the `quickSearch`
 * mutation is resolved by a dedicated Lambda data source that performs
 * vector-similarity search and returns structured results in a single
 * GraphQL response. No subscription, no chunks.
 *
 * Cancellation: pass an `AbortSignal` on the options bag to cancel the
 * in-flight fetch (e.g. when a 3-second timeout fires). The signal is
 * forwarded to `aiGraphqlMutation`, which forwards it to `fetch()`.
 * An aborted request rethrows the underlying `AbortError` so the caller
 * can distinguish cancellation from a real error.
 *
 * Error handling: on transport failure or a GraphQL `errors` payload, this
 * throws rather than returning a sentinel. The `useQuickSearch` hook catches
 * and returns null silently (graceful fallthrough).
 *
 * @param {string} query – user's search query
 * @param {number|null|undefined} maxResults – optional cap on results.
 *      Omit / pass null to let the backend use its default.
 * @param {{ signal?: AbortSignal }} [options] – optional options bag. When
 *      `signal` is provided, it's forwarded to the underlying `fetch()` so
 *      the network request can be cancelled.
 * @returns {Promise<{ data: { quickSearch: { results: Array, result_count: number } } }>}
 *      The AppSync response envelope.
 * @throws the underlying `AbortError` when cancelled, or an `Error` carrying
 *      the GraphQL `errors` payload on a non-transport failure.
 */
export const quickSearch = async (query, maxResults, { signal } = {}) => {
  const mutation = `mutation QuickSearch($input: QuickSearchInput!) {
    quickSearch(input: $input) {
      results {
        parsed { app_name app_id status app_description: description region automation_tools cadence vp process_owner pdd tdd annual_mer_savings Launch_qq_yy use_case rating_avg rating_count view_count url }
        markdown
        score
        source
      }
      result_count
    }
  }`;
  const variables = {
    input: {
      query,
      ...(maxResults != null && { maxResults }),
    },
  };
  const result = await aiGraphqlMutation(mutation, variables, { signal });
  if (result?.errors) {
    const err = new Error('quickSearch returned GraphQL errors');
    err.graphqlErrors = result.errors;
    throw err;
  }
  return result;
};

/**
 * Submit per-turn feedback (thumbs-up / thumbs-down) for a chat or search response.
 *
 * Like `quickSearch`, this path is fully synchronous: the `submitFeedback`
 * mutation is resolved by a dedicated Lambda data source that validates the
 * input and updates the turn record in DynamoDB. No subscription, no chunks.
 *
 * Error handling: on transport failure or a GraphQL `errors` payload, this
 * throws so the `useFeedback` hook can surface the failure as `state: 'error'`.
 * A backend-level validation failure (e.g. turn not found) is NOT thrown — it
 * comes back as `success: false` in the response payload and the hook decides
 * how to present it.
 *
 * @param {{ sessionId: string, conversationId: string, feedback: string, type?: string }} params
 * @param {string} params.sessionId – the session that owns the turn
 * @param {string} params.conversationId – the specific turn to attach feedback to
 * @param {string} params.feedback – `"positive"` or `"negative"`
 * @param {string} [params.type] – `"chat"` or `"search"` (informational, not validated by backend)
 * @returns {Promise<{ data: { submitFeedback: { success: boolean, message: string|null, sessionId: string, conversationId: string } } }>}
 *      The AppSync response envelope.
 * @throws on transport failure or GraphQL errors payload.
 */
export const submitFeedback = async ({ sessionId, conversationId, feedback, type }) => {
  const mutation = `mutation SubmitFeedback($input: SubmitFeedbackInput!) {
    submitFeedback(input: $input) {
      success
      message
      sessionId
      conversationId
    }
  }`;
  const variables = {
    input: {
      sessionId,
      conversationId,
      feedback,
      ...(type && { type }),
    },
  };
  const result = await aiGraphqlMutation(mutation, variables);
  if (result?.errors) {
    const err = new Error('submitFeedback returned GraphQL errors');
    err.graphqlErrors = result.errors;
    throw err;
  }
  return result;
};

/**
 * Subscribe to streamed response chunks for a conversation.
 *
 * @param {string} conversationId – id returned by `sendAgentMessage`
 * @param {(response: { value: { data: { onAgentResponse: object } } }) => void} onResult
 *      – fired for each `data` frame received. The outer `value` wrapper keeps
 *        the shape compatible with Amplify's subscription envelope.
 * @param {(err: any) => void} onFailure – called on protocol / transport error
 *
 * @returns {Promise<{ unsubscribe: () => void }>} — capture this and call
 *      `.unsubscribe()` when the component unmounts or a new subscription
 *      starts, so the underlying WebSocket is torn down cleanly.
 */
export const onAgentResponse = async (conversationId, onResult, onFailure) => {
  // NOTE: `sources` and `correlationId` are populated only on the terminal
  // chunk (status === 'COMPLETE'). Streaming chunks return `null` for both —
  // AppSync returns null for unpopulated selection fields, and the legacy
  // orchestrator (still live during rollout) does not publish them at all,
  // so selecting them here is safe before cutover. (Tasks 9.1 / 2.4 / 6.2)
  const query = `subscription MySubscription($conversationId: String!) {
    onAgentResponse(conversationId: $conversationId) {
      conversationId
      sessionId
      chunkIndex
      content
      status
      correlationId
      sources {
        documentId
        title
        excerpt
        s3Uri
        sourceUrl
        metadata {
          label
          value
        }
      }
    }
  }`;
  try {
    return await aiGraphqlSubscribe(
      query,
      { conversationId },
      (payload) => onResult({ value: payload }),
      onFailure
    );
  } catch (error) {
    if (onFailure) onFailure(error);
    return null;
  }
};
